import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Customer } from "../../../../models";
import { OrderProduct } from '../../../../models';
import { IOrder, IRouteItem, IRouteTiming, Timestamp } from '../../../../types';
import { IOrderProduct } from '../../../../types/OrderProduct';
import { PublicProductModal } from './PublicProductModal';
import { HiOutlinePlus, HiArrowLeft, HiCheck, HiOutlinePencil, HiOutlineTrash, HiLocationMarker, HiChevronLeft, HiChevronRight, HiX, HiOutlineClock } from 'react-icons/hi';
import PublicRouteModal, { PublicRouteSubmitData } from './PublicRouteModal';
import { Button } from '@components';
import { Inline } from "@design-system";
import PublicCustomerModal from './PublicCustomerModal';
import PublicSavedProductsModal from './PublicSavedProductsModal';
import { ICustomer } from '../../../../types';
import { PublicProduct } from '../../../../api/publicOrder';
import { OrderFlow } from './PublicFlowSelection';
import CartHeader from './CartHeader';
import { addVat } from '../../../../utils/pricing';
import { byStopNumber, bySortNr } from '../../../../utils/comparators';
import { createPricingObject, formatLocation } from './publicOrderPricing';

interface PublicOrderFormProps {
    order: IOrder;
    dispatch: React.Dispatch<{ type: string; payload: any }>;
    products: PublicProduct[];
    isLoadingProducts: boolean;
    onSubmit: (e: React.FormEvent) => void;
    isLoading: boolean;
    error: string | null;
    allowProductSelection: boolean;
    config?: any;
    selectedFlow?: OrderFlow;
    onBackToSelection?: () => void;
    accessToken?: string;
    workspaceId?: string;
}

const PublicOrderForm: React.FC<PublicOrderFormProps> = ({
    order,
    dispatch,
    products,
    isLoadingProducts,
    onSubmit,
    isLoading,
    error,
    allowProductSelection,
    config,
    selectedFlow,
    onBackToSelection,
    accessToken,
    workspaceId
}) => {
    const { t } = useTranslation(['order_form']);
    

    
    // Step state for multi-step form
    const [currentStep, setCurrentStep] = useState<number>(1);
    
    // Modal states
    const [isProductModalOpen, setIsProductModalOpen] = useState(false);
    const [isSavedProductsModalOpen, setIsSavedProductsModalOpen] = useState(false);
    const [editingCargoIndex, setEditingCargoIndex] = useState<number | null>(null);
    // Mobile category filter sheet state
    const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

    // Route modal state
    const [isRouteModalOpen, setIsRouteModalOpen] = useState(false);
    const [editingRouteIndex, setEditingRouteIndex] = useState<number | null>(null);

    // Customer modal state
    const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
    
    // Validation state
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    
    // Distance cache for pricing calculations
    const [cachedDistance, setCachedDistance] = useState<number>(0);
    
    // Image carousel state - track current image index for each product
    const [currentImageIndex, setCurrentImageIndex] = useState<Record<string, number>>({});
    
    // Category filter state
    const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);

    // Update cached distance when route changes
    useEffect(() => {
        const updateDistance = async () => {
            if (order.route.length >= 2) {
                // Sort all stops by stop number to get the correct route order
                const sortedStops = [...order.route].sort(byStopNumber);
                
                if (sortedStops.length >= 2) {
                    const distance = await calculateRouteDistance(sortedStops);
                    setCachedDistance(distance);
                } else {
                    setCachedDistance(0);
                }
            } else {
                setCachedDistance(0);
            }
        };
        
        updateDistance();
    }, [order.route]);

    // Update product prices when distance changes
    useEffect(() => {
        // Always update prices when distance changes, even if distance is 0
        // This ensures fixed pricing is applied even when there are fewer than 2 stops
        updateProductPricesWithDistance();
    }, [cachedDistance, products]);

    // Centralized function to calculate product price
    const calculateProductPrice = (product: PublicProduct, quantity: number): number => {
        let productTotal = 0;
        
        // Add fixed price per cargo unit
        if (product.pricing?.pricePerCargoUnit && product.pricing.pricePerCargoUnit > 0) {
            const unitPrice = config?.showPricesWithVat 
                ? addVat(product.pricing.pricePerCargoUnit, product.pricing.vat || 0)
                : product.pricing.pricePerCargoUnit;
            productTotal += unitPrice * quantity;
        }
        
        // Add distance-based pricing if we have cached distance
        if (product.pricing?.pricePerDistanceUnit && product.pricing.pricePerDistanceUnit > 0 && cachedDistance > 0) {
            const unitPrice = config?.showPricesWithVat 
                ? addVat(product.pricing.pricePerDistanceUnit, product.pricing.vat || 0)
                : product.pricing.pricePerDistanceUnit;
            productTotal += unitPrice * cachedDistance * quantity;
        }
        
        // Add volume-based pricing
        if (product.pricing?.pricePerVolumeUnit && product.pricing.pricePerVolumeUnit > 0) {
            const volume = (product.length * product.width * product.height) / 1000000; // Convert cm³ to m³
            if (volume > 0) {
                const unitPrice = config?.showPricesWithVat 
                    ? addVat(product.pricing.pricePerVolumeUnit, product.pricing.vat || 0)
                    : product.pricing.pricePerVolumeUnit;
                productTotal += unitPrice * volume * quantity;
            }
        }
        
        // Add weight-based pricing
        if (product.pricing?.pricePerWeightUnit && product.pricing.pricePerWeightUnit > 0 && product.weight && product.weight > 0) {
            const unitPrice = config?.showPricesWithVat 
                ? addVat(product.pricing.pricePerWeightUnit, product.pricing.vat || 0)
                : product.pricing.pricePerWeightUnit;
            productTotal += unitPrice * product.weight * quantity;
        }
        
        if (product.seatsEnabled && product.pricing?.priceModel?.includes('Seat') && product.seatCount && product.pricing.pricePerSeat) {
            const rate = config?.showPricesWithVat ? addVat(product.pricing.pricePerSeat, product.pricing.vat || 0) : product.pricing.pricePerSeat;
            productTotal += rate * product.seatCount * quantity;
        }
        // Fallback to product.price if no pricing model calculated anything
        if (productTotal === 0) {
            const fallbackPrice = config?.showPricesWithVat 
                ? addVat(product.price || 0, product.pricing?.vat || 0)
                : (product.price || 0);
            productTotal = fallbackPrice * quantity;
        }
        
        return productTotal;
    };

    // Function to update product prices with distance-based pricing
    const updateProductPricesWithDistance = () => {
        order.cargo.forEach((item, index) => {
            if (!item.productId) return;
            
            const product = products.find(p => p.id === item.productId);
            if (!product) return;
            
            const quantity = item.quantity || 1;
            const calculatedPrice = calculateProductPrice(product, quantity);
            
            // Update the product price if it has changed
            if (calculatedPrice !== item.price) {
                const updatedProduct = new OrderProduct({
                    ...item,
                    price: calculatedPrice,
                    pricing: createPricingObject(product)
                });
                dispatch({ type: 'UPDATE_CARGO', payload: { index: index, product: updatedProduct } });
            }
        });
    };

    // Initialize category filters from URL on mount
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const categories = urlParams.get('categories');
        if (categories) {
            setSelectedCategoryIds(categories.split(',').filter(Boolean));
        }
    }, []);

    // Update URL when category selection changes
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (selectedCategoryIds.length > 0) {
            urlParams.set('categories', selectedCategoryIds.join(','));
        } else {
            urlParams.delete('categories');
        }
        
        const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
        window.history.replaceState({}, '', newUrl);
    }, [selectedCategoryIds]);

    // Reset step and local state when flow changes
    useEffect(() => {
        setCurrentStep(1);
        setValidationErrors([]);
        setHasSubmitted(false);
        setCachedDistance(0);
        setCurrentImageIndex({});
        setIsProductModalOpen(false);
        setIsSavedProductsModalOpen(false);
        setIsRouteModalOpen(false);
        setIsCustomerModalOpen(false);
        setEditingCargoIndex(null);
        setEditingRouteIndex(null);
        // Don't reset category filters when flow changes
    }, [selectedFlow]);

    // Scroll to top when step changes (only for product list flow with multiple steps)
    useEffect(() => {
        if (allowProductSelection && currentStep > 1) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [currentStep, allowProductSelection]);

    // Ensure scrolling is enabled when component mounts and unmounts
    useEffect(() => {
        // Reset any potential scroll locks when component mounts
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
        
        return () => {
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
        };
    }, []);

    // Apply global styles for modal buttons and form elements
    useEffect(() => {
        const styleId = 'public-order-form-styles';
        let existingStyle = document.getElementById(styleId);
        
        if (!existingStyle) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = `
                .bg-lgb-primary {
                    background-color: ${config?.primaryColor || '#7214FF'} !important;
                }
                .hover\\:bg-lgb-primary-lighten:hover {
                    background-color: ${config?.primaryColorDark || '#5B0ACD'} !important;
                }
                button[color="primary"]:focus {
                    box-shadow: 0 0 0 2px ${config?.primaryColorLight || '#F1E8FF'} !important;
                }

                /* Radio buttons */
                input[type="radio"] {
                    accent-color: ${config?.primaryColor || '#7214FF'} !important;
                }
                input[type="radio"]:checked {
                    background-color: ${config?.primaryColor || '#7214FF'} !important;
                    border-color: ${config?.primaryColor || '#7214FF'} !important;
                }

                /* Checkboxes */
                input[type="checkbox"] {
                    accent-color: ${config?.primaryColor || '#7214FF'} !important;
                }
                input[type="checkbox"]:checked {
                    background-color: ${config?.primaryColor || '#7214FF'} !important;
                    border-color: ${config?.primaryColor || '#7214FF'} !important;
                }
            `;
            document.head.appendChild(style);
        }
        
        return () => {
            const styleElement = document.getElementById(styleId);
            if (styleElement) {
                styleElement.remove();
            }
        };
    }, [config?.primaryColor, config?.primaryColorDark, config?.primaryColorLight]);

    // Monitor modal state changes and ensure scrolling is enabled when all modals are closed
    useEffect(() => {
        const anyModalOpen = isProductModalOpen || isCustomerModalOpen || isRouteModalOpen || isSavedProductsModalOpen || isMobileFilterOpen;
        
        if (!anyModalOpen) {
            // No modals are open, ensure scrolling is enabled
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
        } else {
            // Lock scroll when a modal/sheet is open
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
        }
    }, [isProductModalOpen, isCustomerModalOpen, isRouteModalOpen, isSavedProductsModalOpen, isMobileFilterOpen]);

    // CSS variables for dynamic colors
    const cssVariables = {
        '--public-form-primary': config?.primaryColor || '#7214FF',
        '--public-form-primary-light': config?.primaryColorLight || '#F1E8FF',
        '--public-form-primary-dark': config?.primaryColorDark || '#5B0ACD',
        '--public-form-primary-ring': (config?.primaryColor || '#7214FF') + '33'
    };

    const isCustomerInfoPresent = !!(order.customer?.name || order.customer?.firstName);

    // Validation functions
    const validateContactInfo = (): boolean => {
        if (!order.customer) return false;
        
        // Check if it's a company or individual
        const isCompany = order.customer.isCompany;
        
        if (isCompany) {
            // Company validation
            return !!(
                order.customer.name?.trim() &&
                order.customer.primaryAddress?.addressLine?.trim() &&
                order.customer.primaryAddress?.postCode?.trim() &&
                order.customer.primaryAddress?.city?.trim() &&
                order.customer.organizationNumber?.trim() &&
                order.customer.contactEmail?.trim() &&
                order.customer.invoiceEmail?.trim() &&
                order.customer.phoneNumber?.trim()
            );
        } else {
            // Individual validation
            return !!(
                order.customer.firstName?.trim() &&
                order.customer.lastName?.trim() &&
                order.customer.primaryAddress?.addressLine?.trim() &&
                order.customer.primaryAddress?.postCode?.trim() &&
                order.customer.primaryAddress?.city?.trim() &&
                order.customer.contactEmail?.trim() &&
                order.customer.phoneNumber?.trim()
            );
        }
    };

    const validateRouteInfo = (): boolean => {
        // Need at least 2 stops (pickup and delivery)
        if (order.route.length < 2) return false;
        
        // Check that each stop has required information
        return order.route.every(stop => 
            stop.location.addressLine?.trim() &&
            stop.location.postCode?.trim() &&
            stop.location.city?.trim()
        );
    };

    const validateOrder = (): { isValid: boolean; errors: string[] } => {
        const errors: string[] = [];
        
        if (!validateContactInfo()) {
            errors.push(t('public_form.validation.contact_info_incomplete'));
        }
        
        if (!validateRouteInfo()) {
            errors.push(t('public_form.validation.route_incomplete'));
        }
        
        // For product list flow, also check if products are selected
        if (allowProductSelection && selectedProducts.length === 0) {
            errors.push(t('public_form.validation.no_products_selected'));
        }
        
        return { isValid: errors.length === 0, errors };
    };

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setHasSubmitted(true);
        
        const validation = validateOrder();
        setValidationErrors(validation.errors);
        
        if (validation.isValid) {
            onSubmit(e);
        } else {
            // Scroll to top to show validation errors
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleOpenProductModal = (index: number | null) => {
        setEditingCargoIndex(index);
        setIsProductModalOpen(true);
    };

    const handleCloseProductModal = () => {
        setIsProductModalOpen(false);
        setEditingCargoIndex(null);
    };

    const handleConfirmCargo = (productData: Partial<IOrderProduct>) => {
        if (editingCargoIndex !== null) {
            const existingProduct = order.cargo[editingCargoIndex];
            
            // Find the product to calculate pricing
            const product = products.find(p => p.id === existingProduct.productId);
            const quantity = productData.quantity || existingProduct.quantity || 1;
            const calculatedPrice = product ? calculateProductPrice(product, quantity) : existingProduct.price || 0;
            
            const updatedProduct = new OrderProduct({ 
                ...existingProduct, 
                ...productData,
                price: calculatedPrice,
                pricing: product ? createPricingObject(product) : existingProduct.pricing,
                // Preserve existing itinerary if not provided in productData
                itinerary: productData.itinerary || existingProduct.itinerary || OrderProduct.defaultItinerary()
            });
            dispatch({ type: 'UPDATE_CARGO', payload: { index: editingCargoIndex, product: updatedProduct } });
        } else {
            // Get default itinerary based on available stops for new products
            const pickupStops = getPickupStops();
            const deliveryStops = getDeliveryStops();
            const defaultItinerary = OrderProduct.defaultItinerary();
            
            // For new products, we need to find the product to calculate pricing
            const product = products.find(p => p.id === productData.productId);
            const quantity = productData.quantity || 1;
            const calculatedPrice = product ? calculateProductPrice(product, quantity) : 0;
            
            const newProduct = new OrderProduct({ 
                ...OrderProduct.defaultProduct(), 
                ...productData,
                price: calculatedPrice,
                pricing: product ? createPricingObject(product) : undefined,
                itinerary: {
                    pickupStopNumber: pickupStops.length > 0 ? pickupStops[0].stopNumber : defaultItinerary.pickupStopNumber,
                    deliveryStopNumber: deliveryStops.length > 0 ? deliveryStops[0].stopNumber : defaultItinerary.deliveryStopNumber
                }
            });
            dispatch({ type: 'ADD_CARGO', payload: { product: newProduct } });
        }
        handleCloseProductModal();
    };

    const handleOpenCustomerModal = () => setIsCustomerModalOpen(true);
    const handleCloseCustomerModal = () => setIsCustomerModalOpen(false);

    const handleConfirmCustomer = (customerData: Partial<ICustomer>) => {
        dispatch({ type: 'UPDATE_CUSTOMER', payload: { value: customerData } });
        handleCloseCustomerModal();
    };

    const handleOpenRouteModal = (index: number | null) => {
        setEditingRouteIndex(index);
        setIsRouteModalOpen(true);
    };

    const handleCloseRouteModal = () => {
        setIsRouteModalOpen(false);
        setEditingRouteIndex(null);
    };

    const handleConfirmRoute = (data: PublicRouteSubmitData) => { 
        // Note: Product pricing will be automatically updated when the route changes
        // via the useEffect that watches order.route and cachedDistance
        
        let timing: IRouteTiming;
        switch (data.timing) {
            case "1": 
                timing = { 
                    earliest: false, 
                    latest: false, 
                    before: data.beforeTime || "17:00" 
                }; 
                break;
            case "2": 
                timing = { 
                    earliest: false, 
                    latest: false, 
                    before: data.beforeTime || "17:00", 
                    after: data.afterTime || "08:00" 
                }; 
                break;
            case "3": 
                timing = { 
                    earliest: false, 
                    latest: false, 
                    after: data.afterTime || "08:00" 
                }; 
                break;
            case "4": 
                timing = { 
                    earliest: true, 
                    latest: false 
                }; 
                break;
            case "5": 
                timing = { 
                    earliest: false, 
                    latest: true 
                }; 
                break;
            default: 
                timing = { 
                    earliest: true, 
                    latest: false 
                };
        }

        if (editingRouteIndex !== null) {
            const routeItem = order.route[editingRouteIndex];
            const updatedRouteItem: IRouteItem = {
                ...routeItem,
                location: { ...routeItem.location, ...data.location, displayName: data.location.displayName || data.location.addressLine || '' },
                isPickup: data.isPickup,
                stopDate: Timestamp.fromDate(data.stopDate),
                timing,
            };
            dispatch({ type: 'UPDATE_ROUTE', payload: { index: editingRouteIndex, routeItem: updatedRouteItem } });
        } else {
            const newRouteItem: IRouteItem = {
                location: {
                    id: '',
                    workspaceId: '',
                    customerId: '',
                    createdAt: null,
                    postCode: '',
                    city: '',
                    countryCode: 'NO',
                    ...data.location,
                    displayName: data.location.displayName || data.location.addressLine || '',
                },
                stopNumber: order.route.length,
                isPickup: data.isPickup,
                stopDate: Timestamp.fromDate(data.stopDate),
                timing,
            };
            dispatch({ type: 'ADD_ROUTE', payload: { routeItem: newRouteItem } });
        }
        handleCloseRouteModal();
    }

    const handleRemoveRoute = (index: number) => {
        dispatch({ type: 'REMOVE_ROUTE', payload: { index } });
    };

    const getCargoToEdit = (): IOrderProduct => {
        if (editingCargoIndex !== null) {
            return order.cargo[editingCargoIndex];
        }
        return OrderProduct.defaultProduct();
    };

        const getRouteItemToEdit = (): Partial<IRouteItem> => {
        if (editingRouteIndex !== null) {
            const existingItem = order.route[editingRouteIndex];
            return existingItem;
        }
        
        // Simple position-based default:
        // - First stop (index 0): default to pickup (true)
        // - All subsequent stops: default to delivery (false)
        const defaultIsPickup = order.route.length === 0;
                
        const defaultItem = {
            isPickup: defaultIsPickup,
            stopDate: Timestamp.now(),
        };
        
        return defaultItem;
    };

    const handleAddSelectedProducts = (selectedProducts: { product: PublicProduct; quantity: number }[]) => {
        // Get default itinerary based on available stops
        const pickupStops = getPickupStops();
        const deliveryStops = getDeliveryStops();
        const defaultItinerary = OrderProduct.defaultItinerary();
        
        selectedProducts.forEach(({ product, quantity }) => {
            const calculatedPrice = calculateProductPrice(product, quantity);
            
            const newProduct = new OrderProduct({
                ...OrderProduct.defaultProduct(),
                descriptionShort: product.descriptionShort,
                description: product.description,
                weight: product.weight * quantity,
                length: product.length,
                width: product.width,
                seatsEnabled: product.seatsEnabled, seatCount: product.seatCount,
                height: product.height,
                quantity: quantity,
                productId: product.id,
                price: calculatedPrice,
                pricing: createPricingObject(product),
                itinerary: {
                    pickupStopNumber: pickupStops.length > 0 ? pickupStops[0].stopNumber : defaultItinerary.pickupStopNumber,
                    deliveryStopNumber: deliveryStops.length > 0 ? deliveryStops[0].stopNumber : defaultItinerary.deliveryStopNumber
                }
            });
            
            dispatch({ type: 'ADD_CARGO', payload: { product: newProduct } });
        });
        setIsSavedProductsModalOpen(false);
    };

    const handleToggleProduct = (productId: string) => {
        const product = products.find(p => p.id === productId);
        if (!product) return;

        const isSelected = isProductSelected(productId);
        
        if (isSelected) {
            // Remove product from cargo
            const filteredCargo = order.cargo.filter(item => item.productId !== productId);
            dispatch({ type: 'SET_CARGO', payload: { cargo: filteredCargo } });
        } else {
            // Add product to cargo
            const quantity = 1;
            const calculatedPrice = calculateProductPrice(product, quantity);
            
            // Get default itinerary based on available stops
            const pickupStops = getPickupStops();
            const deliveryStops = getDeliveryStops();
            const defaultItinerary = OrderProduct.defaultItinerary();
            
            const newProduct = new OrderProduct({
                ...OrderProduct.defaultProduct(),
                descriptionShort: product.descriptionShort,
                description: product.description,
                weight: product.weight,
                length: product.length,
                width: product.width,
                seatsEnabled: product.seatsEnabled, seatCount: product.seatCount,
                height: product.height,
                quantity: quantity,
                productId: product.id,
                price: calculatedPrice,
                pricing: createPricingObject(product),
                itinerary: {
                    pickupStopNumber: pickupStops.length > 0 ? pickupStops[0].stopNumber : defaultItinerary.pickupStopNumber,
                    deliveryStopNumber: deliveryStops.length > 0 ? deliveryStops[0].stopNumber : defaultItinerary.deliveryStopNumber
                }
            });
            
            dispatch({ type: 'ADD_CARGO', payload: { product: newProduct } });
        }
    };

    const handleQuantityChange = (productId: string, newQuantity: number) => {
        const quantity = Math.max(1, newQuantity);
        const cargoIndex = order.cargo.findIndex(item => item.productId === productId);
        if (cargoIndex !== -1) {
            const product = products.find(p => p.id === productId);
            if (product) {
                const calculatedPrice = calculateProductPrice(product, quantity);
                
                const updatedProduct = new OrderProduct({
                    ...order.cargo[cargoIndex],
                    quantity: quantity,
                    weight: product.weight * quantity,
                    price: calculatedPrice,
                    pricing: createPricingObject(product)
                });
                dispatch({ type: 'UPDATE_CARGO', payload: { index: cargoIndex, product: updatedProduct } });
            }
        }
    };

    // Handler for updating pickup location
    const handlePickupLocationChange = (productId: string, pickupStopNumber: number) => {
        const cargoIndex = order.cargo.findIndex(item => item.productId === productId);
        
        if (cargoIndex !== -1) {
            const currentProduct = order.cargo[cargoIndex];
            const defaultItinerary = OrderProduct.defaultItinerary();
            const updatedProduct = new OrderProduct({
                ...currentProduct,
                itinerary: {
                    pickupStopNumber: pickupStopNumber,
                    deliveryStopNumber: currentProduct.itinerary?.deliveryStopNumber ?? defaultItinerary.deliveryStopNumber
                }
            });
            dispatch({ type: 'UPDATE_CARGO', payload: { index: cargoIndex, product: updatedProduct } });
        }
    };

    // Handler for updating delivery location
    const handleDeliveryLocationChange = (productId: string, deliveryStopNumber: number) => {
        const cargoIndex = order.cargo.findIndex(item => item.productId === productId);
        
        if (cargoIndex !== -1) {
            const currentProduct = order.cargo[cargoIndex];
            const defaultItinerary = OrderProduct.defaultItinerary();
            const updatedProduct = new OrderProduct({
                ...currentProduct,
                itinerary: {
                    pickupStopNumber: currentProduct.itinerary?.pickupStopNumber ?? defaultItinerary.pickupStopNumber,
                    deliveryStopNumber: deliveryStopNumber
                }
            });
            dispatch({ type: 'UPDATE_CARGO', payload: { index: cargoIndex, product: updatedProduct } });
        }
    };

    // Helper function to get pickup stops (isPickup = true)
    const getPickupStops = () => {
        return order.route
            .map((stop, index) => ({ ...stop, originalIndex: index }))
            .filter(stop => stop.isPickup === true)
            .sort(byStopNumber);
    };

    // Helper function to get delivery stops (isPickup = false)
    const getDeliveryStops = () => {
        return order.route
            .map((stop, index) => ({ ...stop, originalIndex: index }))
            .filter(stop => stop.isPickup === false)
            .sort(byStopNumber);
    };

    // Helper function to format stop display name
    const formatStopDisplayName = (stop: IRouteItem) => {
        const locationName = stop.location.displayName || stop.location.addressLine || t('public_form.common.unknown_location');
        const address = [stop.location.addressLine, stop.location.postCode, stop.location.city]
            .filter(Boolean)
            .join(', ');
        
        return {
            value: stop.stopNumber,
            label: locationName,
            subtitle: address
        };
    };

    const isProductSelected = (productId: string): boolean => {
        return selectedProducts.some(product => product.productId === productId);
    };

    // Image carousel navigation
    const handlePrevImage = (productId: string, totalImages: number) => {
        setCurrentImageIndex(prev => ({
            ...prev,
            [productId]: prev[productId] > 0 ? prev[productId] - 1 : totalImages - 1
        }));
    };

    const handleNextImage = (productId: string, totalImages: number) => {
        setCurrentImageIndex(prev => ({
            ...prev,
            [productId]: prev[productId] < totalImages - 1 ? (prev[productId] || 0) + 1 : 0
        }));
    };

    const goToImage = (productId: string, imageIndex: number) => {
        setCurrentImageIndex(prev => ({
            ...prev,
            [productId]: imageIndex
        }));
    };

    // Format price with Norwegian currency formatting
    const formatPrice = (price: number | null | undefined) => {
        if (price === null || price === undefined) return `0,00 ${t('public_form.common.currency')}`;
        
        return new Intl.NumberFormat('nb-NO', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(price).replace('kr', t('public_form.common.currency'));
    };

    // Calculate total distance for entire route through all stops
    const calculateRouteDistance = async (sortedStops: IRouteItem[]): Promise<number> => {
        try {
            if (sortedStops.length < 2) return 0;

            const origin = formatLocation(sortedStops[0]);
            const destination = formatLocation(sortedStops[sortedStops.length - 1]);
            const intermediates = sortedStops.slice(1, -1).map(stop => formatLocation(stop));

            const requestBody: any = {
                origin,
                destination,
                travelMode: 'DRIVE'
            };

            if (intermediates.length > 0) {
                requestBody.intermediates = intermediates;
            }

            const response = await fetch('https://routes.googleapis.com/directions/v2:computeRoutes', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Goog-Api-Key': process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
                    'X-Goog-FieldMask': 'routes.distanceMeters,routes.duration'
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                console.error('Google Routes API error:', response.status, response.statusText);
                return 0;
            }

            const data = await response.json();
            if (data.routes && data.routes[0] && data.routes[0].distanceMeters) {
                // Convert meters to kilometers
                return data.routes[0].distanceMeters / 1000;
            }
            
            return 0;
        } catch (error) {
            console.error('Error calculating route distance:', error);
            return 0;
        }
    };

    // Calculate total price including distance-based pricing
    const calculateTotalPrice = (): number => {
        let total = 0;
        
        selectedProducts.forEach(item => {
            // Use the stored price which already includes all calculations
            total += item.price || 0;
        });
        
        return total;
    };

    const selectedProducts = order.cargo.filter(item => item.productId);

    // Get available categories based on config and products
    const availableCategories = useMemo(() => {
        if (!config?.enableCategoryFilter || !config?.categoryFilters || config.categoryFilters.length === 0) {
            return [];
        }
        
        // Sort categories by sortNr from config
        const sortedCategories = [...config.categoryFilters].sort(bySortNr);
        
        // Only show categories that actually have products
        const availableCategories = sortedCategories.filter(category => 
            products.some(product => 
                product.productType && Array.isArray(product.productType) 
                    ? product.productType.includes(category.id)
                    : product.productType === category.id
            )
        );
        
        return availableCategories;
    }, [config, products]);

    // Sort products based on config productOrder
    const sortedProducts = useMemo(() => {
        if (!config?.productOrder || config.productOrder.length === 0) {
            return products;
        }
        
        // Create a map for quick lookup of sort order
        const orderMap = new Map(config.productOrder.map((item: any) => [item.id, item.sortNr]));
        
        // Sort products: configured products first (by sortNr), then unconfigured products
        return [...products].sort((a, b) => {
            const aSortNr = orderMap.get(a.id);
            const bSortNr = orderMap.get(b.id);
            
            // Both have order - sort by sortNr
            if (aSortNr !== undefined && bSortNr !== undefined) {
                return (aSortNr as number) - (bSortNr as number);
            }
            
            // Only a has order - a comes first
            if (aSortNr !== undefined && bSortNr === undefined) {
                return -1;
            }
            
            // Only b has order - b comes first
            if (aSortNr === undefined && bSortNr !== undefined) {
                return 1;
            }
            
            // Neither has order - maintain original order
            return 0;
        });
    }, [products, config?.productOrder]);

    // Filter products based on selected categories
    const filteredProducts = useMemo(() => {
        const baseProducts = sortedProducts;
        
        if (!config?.enableCategoryFilter || selectedCategoryIds.length === 0) {
            return baseProducts;
        }
        
        return baseProducts.filter(product => 
            product.productType && (
                Array.isArray(product.productType) 
                    ? product.productType.some(typeId => selectedCategoryIds.includes(typeId))
                    : selectedCategoryIds.includes(product.productType)
            )
        );
    }, [sortedProducts, selectedCategoryIds, config?.enableCategoryFilter]);

    // Category filter handlers
    const handleCategoryToggle = (categoryId: string) => {
        setSelectedCategoryIds(prev => {
            if (prev.includes(categoryId)) {
                return prev.filter(id => id !== categoryId);
            } else {
                return [...prev, categoryId];
            }
        });
    };

    const handleClearAllCategories = () => {
        setSelectedCategoryIds([]);
    };

    // Helper function to format pricing information
    const formatProductPricing = (product: PublicProduct): JSX.Element => {
        if (!product.pricing || !product.pricing.priceModel || product.pricing.priceModel.length === 0) {
            // Fallback to simple price if no pricing model
            if (product.price && product.price > 0) {
                const vatRate = product.pricing?.vat || 0;
                const basePrice = product.price;
                const displayPrice = config?.showPricesWithVat && vatRate > 0 ? basePrice * (1 + vatRate / 100) : basePrice;
                
                return <div className="text-right"><span className="public-form-primary-text font-semibold text-base">{displayPrice.toFixed(2)} {t('public_form.common.currency')}</span></div>;
            }
            return <div className="text-right"><span className="text-muted text-base">{t('public_form.common.price_on_request')}</span></div>;
        }

        const activePrices: string[] = [];
        const { pricing } = product;
        const vatRate = pricing.vat || 0;
        const showWithVat = config?.showPricesWithVat ?? true;

        // Check each price model and add active ones
        if (pricing.priceModel.includes('Fixed') && pricing.pricePerCargoUnit && pricing.pricePerCargoUnit > 0) {
            const basePrice = pricing.pricePerCargoUnit;
            const displayPrice = showWithVat && vatRate > 0 ? basePrice * (1 + vatRate / 100) : basePrice;
            activePrices.push(`${displayPrice.toFixed(2)} ${t('public_form.common.currency')}/${t('public_form.common.pieces_short')}`);
        }
        
        if (pricing.priceModel.includes('Distance') && pricing.pricePerDistanceUnit && pricing.pricePerDistanceUnit > 0) {
            const basePrice = pricing.pricePerDistanceUnit;
            const displayPrice = showWithVat && vatRate > 0 ? basePrice * (1 + vatRate / 100) : basePrice;
            activePrices.push(`${displayPrice.toFixed(2)} ${t('public_form.common.currency')}/${t('public_form.common.km')}`);
        }
        
        if (pricing.priceModel.includes('Weight') && pricing.pricePerWeightUnit && pricing.pricePerWeightUnit > 0) {
            const basePrice = pricing.pricePerWeightUnit;
            const displayPrice = showWithVat && vatRate > 0 ? basePrice * (1 + vatRate / 100) : basePrice;
            activePrices.push(`${displayPrice.toFixed(2)} ${t('public_form.common.currency')}/${t('public_form.common.kg')}`);
        }
        
        if (pricing.priceModel.includes('Volume') && pricing.pricePerVolumeUnit && pricing.pricePerVolumeUnit > 0) {
            const basePrice = pricing.pricePerVolumeUnit;
            const displayPrice = showWithVat && vatRate > 0 ? basePrice * (1 + vatRate / 100) : basePrice;
            activePrices.push(`${displayPrice.toFixed(2)} ${t('public_form.common.currency')}/${t('public_form.common.m3')}`);
        }
        
        if (pricing.priceModel.includes('Time') && pricing.pricePerTimeUnit && pricing.pricePerTimeUnit > 0) {
            const basePrice = pricing.pricePerTimeUnit;
            const displayPrice = showWithVat && vatRate > 0 ? basePrice * (1 + vatRate / 100) : basePrice;
            const timePeriod = pricing.timePeriodInMinutes || 60;
            const timeUnit = timePeriod === 1 ? t('public_form.common.minute') : 
                            timePeriod === 60 ? t('public_form.common.hour') : 
                            `${timePeriod} ${t('public_form.common.minutes_short')}`;
            activePrices.push(`${displayPrice.toFixed(2)} ${t('public_form.common.currency')}/${timeUnit}`);
        }

        if (activePrices.length === 0) {
            return <div className="text-right"><span className="text-muted text-base">{t('public_form.common.price_on_request')}</span></div>;
        }

        const vatText = vatRate > 0 ? 
            (showWithVat ? t('public_form.common.incl_vat', { vat: vatRate }) : t('public_form.common.excl_vat', { vat: vatRate })) : 
            '';

        return (
            <div className="space-y-1 text-right">
                {activePrices.map((priceText, index) => (
                    <div key={index} className="public-form-primary-text font-semibold text-base">
                        {priceText}
                    </div>
                ))}
                {vatText && (
                    <div className="text-gray-500 text-xs">{vatText}</div>
                )}
            </div>
        );
    };

    const handleProceedToDetails = () => {
        if (currentStep === 1) {
            setCurrentStep(2);
        } else {
            setCurrentStep(1);
        }
    };

    const handleBackToProducts = () => {
        setCurrentStep(1);
    };

    // For product list flow: need selected products to proceed
    // For custom offer flow: can proceed without products
    const canProceedToDetails = allowProductSelection ? selectedProducts.length > 0 : true;

    // Determine if workspace config defines a fixed pickup point
    const hasDefaultPickup = !!config?.defaultPickupLocation;

    // Route items to display to user (hide default pickup)
    const visibleRoute = order.route
        .map((item, routeIndex) => ({ item, routeIndex }))
        .filter(({ item, routeIndex }) => !(hasDefaultPickup && item.isPickup && routeIndex === 0));

    return (
        <div 
            className="min-h-screen bg-gray-50 flex flex-col" 
            style={cssVariables as React.CSSProperties}
        >
            {/* Fixed Logo Header */}
            {config?.logoUrl && config.logoUrl.trim() !== "" && (
                <div className="fixed top-0 left-0 right-0 z-50 border-b border-gray-200" style={{ backgroundColor: config?.headerColor || '#FFFFFF' }}>
                    <div className="max-w-7xl mx-auto px-4 py-3">
                        <div className="flex justify-between items-center">
                            <div>
                                {config.logoRedirectUrl && config.logoRedirectUrl.trim() !== "" ? (
                                    <a href={config.logoRedirectUrl}>
                                        <img
                                            src={config.logoUrl}
                                            alt="Company logo"
                                            className="h-12 max-w-48 object-contain"
                                            style={{ cursor: 'pointer' }}
                                        />
                                    </a>
                                ) : (
                                    <img
                                        src={config.logoUrl}
                                        alt="Company logo"
                                        className="h-12 max-w-48 object-contain"
                                    />
                                )}
                            </div>
                            
                            {/* Cart Header - only show for product list flow */}
                            {allowProductSelection && (
                                <CartHeader
                                    selectedProducts={selectedProducts}
                                    products={products}
                                    onProceedToDetails={handleProceedToDetails}
                                    canProceedToDetails={canProceedToDetails}
                                    currentStep={currentStep}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
            
            <div className={`flex-1 ${config?.logoUrl && config.logoUrl.trim() !== "" ? 'pt-20' : 'py-10'} mt-8 pb-8`}>
            <style>
                {`
                    .public-form-primary-bg {
                        background-color: var(--public-form-primary);
                    }
                    .public-form-primary-light-bg {
                        background-color: var(--public-form-primary-light);
                    }
                    .public-form-primary-text {
                        color: var(--public-form-primary);
                    }
                    .public-form-primary-border {
                        border-color: var(--public-form-primary);
                    }
                    .public-form-primary-ring {
                        --tw-ring-color: var(--public-form-primary-ring);
                    }
                    .public-form-primary-hover:hover {
                        background-color: var(--public-form-primary-dark);
                    }
                `}
            </style>
            {isProductModalOpen && (
                <PublicProductModal
                    data={getCargoToEdit()}
                    isShowing={isProductModalOpen}
                    onConfirm={handleConfirmCargo}
                    onCancel={handleCloseProductModal}
                    config={config}
                />
            )}
            <PublicSavedProductsModal
                isOpen={isSavedProductsModalOpen}
                onClose={() => setIsSavedProductsModalOpen(false)}
                onConfirm={handleAddSelectedProducts}
                products={products}
                isLoading={isLoadingProducts}
                config={config}
            />
            {isRouteModalOpen && (
                <PublicRouteModal
                    isOpen={isRouteModalOpen}
                    onClose={handleCloseRouteModal}
                    onConfirm={handleConfirmRoute}
                    data={getRouteItemToEdit()}
                    title={editingRouteIndex !== null ? t('public_form.common.edit_stop') : t('public_form.common.add_stop')}
                    config={config}
                    allowPickupSelection={!config?.defaultPickupLocation}
                    accessToken={accessToken}
                    workspaceId={workspaceId}
                    customer={order.customer}
                />
            )}
            {/* Mobile Category Filter Bottom Sheet */}
            {isMobileFilterOpen && availableCategories.length > 0 && (
                <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
                    <div 
                        className="absolute inset-0 bg-black/30"
                        onClick={() => setIsMobileFilterOpen(false)}
                        aria-label={t('common:close', 'Close')}
                    />
                    <div 
                        className="absolute inset-x-0 bottom-0 bg-white rounded-t-2xl shadow-xl max-h-[80vh] overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between p-4 border-b border-gray-200">
                            <h3 className="text-base font-semibold text-gray-900">
                                {t('order_form:category_filter.filters', 'Filters')}
                            </h3>
                            <button
                                type="button"
                                onClick={() => setIsMobileFilterOpen(false)}
                                className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none"
                                aria-label={t('common:close', 'Close')}
                            >
                                <HiX className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="p-4 overflow-y-auto max-h-[60vh]">
                            <div className="space-y-2">
                                {availableCategories.map(category => {
                                    const isSelected = selectedCategoryIds.includes(category.id);
                                    const productCount = products.filter(p => 
                                        p.productType && (
                                            Array.isArray(p.productType) 
                                                ? p.productType.includes(category.id)
                                                : p.productType === category.id
                                        )
                                    ).length;
                                    return (
                                        <label
                                            key={category.id}
                                            className="flex items-start gap-3 py-2 cursor-pointer"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => handleCategoryToggle(category.id)}
                                                className="h-4 w-4 mt-0.5 rounded border-gray-300 public-form-primary-text focus:ring-0 focus:outline-none flex-shrink-0 cursor-pointer"
                                            />
                                            <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
                                                <span className="text-sm font-medium text-gray-700 leading-5 overflow-hidden flex-1"
                                                      style={{
                                                          display: '-webkit-box',
                                                          WebkitBoxOrient: 'vertical',
                                                          WebkitLineClamp: 2,
                                                          textOverflow: 'ellipsis'
                                                      }}>
                                                    {category.name}
                                                </span>
                                                <span className="text-sm text-gray-500 flex-shrink-0">({productCount})</span>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                        <Inline gap={3} justify="between" className="p-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={handleClearAllCategories}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                            >
                                {t('order_form:category_filter.clear_all', 'Clear all')}
                            </button>
                            <button
                                type="button"
                                onClick={() => setIsMobileFilterOpen(false)}
                                className="px-4 py-2 text-sm font-medium text-white rounded-md public-form-primary-bg public-form-primary-hover"
                            >
                                {t('public_form.common.apply', 'Apply')}
                            </button>
                        </Inline>
                    </div>
                </div>
            )}
            {isCustomerModalOpen && (
                <PublicCustomerModal
                    isOpen={isCustomerModalOpen}
                    onClose={handleCloseCustomerModal}
                    onConfirm={handleConfirmCustomer}
                    data={order.customer}
                    config={config}
                    accessToken={accessToken}
                    workspaceId={workspaceId}
                />
            )}
            <div className="max-w-7xl mx-auto px-4">
                {/* Navigation */}
                <div className="mb-8 flex items-center justify-between">
                    {(onBackToSelection && currentStep === 1) ? (
                        <button
                            onClick={onBackToSelection}
                            className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800 transition-all duration-200 shadow-sm"
                            title={t('common:back')}
                        >
                            <HiArrowLeft className="h-5 w-5" />
                        </button>
                    ) : currentStep === 2 ? (
                        <button
                            onClick={handleBackToProducts}
                            className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800 transition-all duration-200 shadow-sm"
                            title={t('public_form.common.back_to_products')}
                        >
                            <HiArrowLeft className="h-5 w-5" />
                        </button>
                    ) : (
                        <div></div>
                    )}
                    
                    {/* Header in center */}
                    <h1 className="text-2xl font-bold tracking-tight text-gray-900">
                        {currentStep === 1 ? (
                            selectedFlow === 'product-list' 
                                ? (config?.productListFlowTitle || t('public_form.form.title'))
                                : (config?.customOrderFlowTitle || t('public_form.form.title'))
                        ) : (
                            t('public_form.common.contact_and_transport_info')
                        )}
                    </h1>
                    
                    {/* Step indicator - only show for product list flow */}
                    {allowProductSelection ? (
                        <div className="flex items-center space-x-2">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                currentStep === 1 ? 'public-form-primary-bg text-white' : 'bg-gray-200 text-gray-600'
                            }`}>
                                1
                            </div>
                            <div className="w-8 h-0.5 bg-gray-200"></div>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                currentStep === 2 ? 'public-form-primary-bg text-white' : 'bg-gray-200 text-gray-600'
                            }`}>
                                2
                            </div>
                        </div>
                    ) : (
                        <div></div>
                    )}
                </div>

                {error && (
                    <div className="mb-8 p-4 bg-red-50 border border-red-200 text-red-600 rounded-lg">
                        {error}
                    </div>
                )}

                {/* Validation Errors - only show on checkout step or single-step flow */}
                {hasSubmitted && validationErrors.length > 0 && (currentStep === 2 || !allowProductSelection) && (
                    <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <h3 className="text-red-800 font-medium mb-2">{t('public_form.validation.title')}</h3>
                        <ul className="list-disc list-inside space-y-1">
                            {validationErrors.map((error, index) => (
                                <li key={index} className="text-red-600 text-sm">{error}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Step 1: Product Selection */}
                {currentStep === 1 && (
                    <div className="space-y-12">
                        {/* Products Grid Section */}
                        {allowProductSelection && products.length > 0 && (
                            <div>
                                {isLoadingProducts ? (
                                    <div className="flex items-center justify-center py-12">
                                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                                    </div>
                                ) : (
                                    <div className="flex gap-6">
                                        {/* Category Filter Sidebar */}
                                        {availableCategories.length > 0 && (
                                            <div className="hidden lg:block w-64 flex-shrink-0">
                                                <div className="p-6 sticky top-24">
                                                    <div className="flex items-center justify-between mb-4">
                                                        <h3 className="text-lg font-semibold text-gray-900">
                                                            {t('order_form:category_filter.categories')}
                                                        </h3>
                                                        {selectedCategoryIds.length > 0 && (
                                                            <button
                                                                type="button"
                                                                onClick={handleClearAllCategories}
                                                                className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
                                                            >
                                                                <HiX className="h-4 w-4 mr-1" />
                                                                {t('order_form:category_filter.clear_all')}
                                                            </button>
                                                        )}
                                                    </div>
                                                    
                                                    <div className="space-y-1">
                                                        {availableCategories.map(category => {
                                                            const isSelected = selectedCategoryIds.includes(category.id);
                                                            const productCount = products.filter(p => 
                                                                p.productType && (
                                                                    Array.isArray(p.productType) 
                                                                        ? p.productType.includes(category.id)
                                                                        : p.productType === category.id
                                                                )
                                                            ).length;
                                                            
                                                            return (
                                                                <label
                                                                    key={category.id}
                                                                    className="flex items-start gap-3 py-2 cursor-pointer"
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isSelected}
                                                                        onChange={() => handleCategoryToggle(category.id)}
                                                                        className="h-4 w-4 mt-0.5 rounded border-gray-300 public-form-primary-text focus:ring-0 focus:outline-none flex-shrink-0 cursor-pointer"
                                                                    />
                                                                    <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
                                                                        <span className="text-sm font-medium text-gray-700 leading-5 overflow-hidden flex-1" 
                                                                              style={{
                                                                                  display: '-webkit-box',
                                                                                  WebkitBoxOrient: 'vertical',
                                                                                  WebkitLineClamp: 2,
                                                                                  textOverflow: 'ellipsis'
                                                                              }}>
                                                                            {category.name}
                                                                        </span>
                                                                        <span className="text-sm text-gray-500 flex-shrink-0">
                                                                            ({productCount})
                                                                        </span>
                                                                    </div>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Products Grid */}
                                        <div className="flex-1">
                                            {/* Mobile Filter Button */}
                                            {availableCategories.length > 0 && (
                                                <div className="mb-4 lg:hidden">
                                                    <button
                                                        type="button"
                                                        onClick={() => setIsMobileFilterOpen(true)}
                                                        className="inline-flex items-center px-3 py-2 rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 public-form-primary-ring"
                                                        aria-label={t('order_form:category_filter.filters', 'Filters')}
                                                    >
                                                        {t('order_form:category_filter.filters', 'Filters')}
                                                        {selectedCategoryIds.length > 0 && (
                                                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                                                {selectedCategoryIds.length}
                                                            </span>
                                                        )}
                                                    </button>
                                                </div>
                                            )}
                                            {selectedCategoryIds.length > 0 && filteredProducts.length === 0 ? (
                                                <div className="text-center py-12">
                                                    <div className="text-gray-500 text-lg mb-2">{t('order_form:category_filter.no_products_found')}</div>
                                                    <p className="text-muted text-sm">
                                                        {t('order_form:category_filter.try_different_categories')} 
                                                        <button 
                                                            type="button"
                                                            onClick={handleClearAllCategories}
                                                            className="ml-1 text-blue-600 hover:text-blue-700 underline"
                                                        >
                                                            {t('order_form:category_filter.clear_all_filters')}
                                                        </button>
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                                    {filteredProducts.map(product => {
                                            const isSelected = isProductSelected(product.id);
                                            return (
                                                <div 
                                                    key={product.id} 
                                                    className={`bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 flex flex-col h-full border-2 overflow-hidden ${
                                                        isSelected 
                                                            ? 'public-form-primary-border ring-2 public-form-primary-ring' 
                                                            : 'border-gray-200 hover:border-gray-300'
                                                    }`}
                                                >
                                                    {/* Product Image Carousel with absolute selected indicator */}
                                                    {product.imgUrls && product.imgUrls.length > 0 ? (
                                                        <div className="relative group">
                                                            <img 
                                                                src={product.imgUrls[currentImageIndex[product.id] || 0]} 
                                                                alt={product.descriptionShort}
                                                                className="w-full h-40 object-cover"
                                                                onError={(e) => {
                                                                    e.currentTarget.style.display = 'none';
                                                                }}
                                                            />
                                                            
                                                            {/* Navigation arrows - only show if multiple images */}
                                                            {product.imgUrls.length > 1 && (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handlePrevImage(product.id, product.imgUrls.length);
                                                                        }}
                                                                        className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-opacity-70"
                                                                    >
                                                                        <HiChevronLeft className="h-4 w-4" />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleNextImage(product.id, product.imgUrls.length);
                                                                        }}
                                                                        className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-opacity-70"
                                                                    >
                                                                        <HiChevronRight className="h-4 w-4" />
                                                                    </button>
                                                                </>
                                                            )}
                                                            
                                                            {/* Image indicators dots - only show if multiple images */}
                                                            {product.imgUrls.length > 1 && (
                                                                <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
                                                                    {product.imgUrls.map((_, index) => (
                                                                        <button
                                                                            key={index}
                                                                            type="button"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                goToImage(product.id, index);
                                                                            }}
                                                                            className={`w-2 h-2 rounded-full transition-all hover:scale-125 ${
                                                                                index === (currentImageIndex[product.id] || 0)
                                                                                    ? 'bg-white'
                                                                                    : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                                                                            }`}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            )}
                                                            
                                                            {/* Selected indicator positioned over image */}
                                                        {isSelected && (
                                                                <div className="absolute top-2 right-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-white public-form-primary-text shadow-sm">
                                                                <HiCheck className="h-3 w-3 mr-1" />
                                                                {t('public_form.common.selected')}
                                                            </div>
                                                        )}
                                                    </div>
                                                    ) : (
                                                        /* If no image, show selected indicator in top right corner */
                                                        <div className="relative h-6">
                                                            {isSelected && (
                                                                <div className="absolute top-2 right-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium public-form-primary-light-bg public-form-primary-text">
                                                                    <HiCheck className="h-3 w-3 mr-1" />
                                                                    {t('public_form.common.selected')}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                    
                                                    <div className="flex-1 mb-4 p-6 pt-4">
                                                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{product.descriptionShort}</h3>
                                                        <p className="text-sm text-gray-600 mb-4">{product.description}</p>
                                                        
                                                        <div className="space-y-2 text-xs text-secondary">
                                                            {/* Only show dimensions if they have real values */}
                                                            {(product.length > 0 || product.width > 0 || product.height > 0) && (
                                                            <div className="flex justify-between">
                                                                <span className="font-medium">{t('public_form.common.dimensions')}:</span>
                                                                <span>{product.length}×{product.width}×{product.height} {t('public_form.common.cm')}</span>
                                                            </div>
                                                            )}
                                                            {/* Only show weight if it has a real value */}
                                                            {product.weight > 0 && (
                                                            <div className="flex justify-between">
                                                                <span className="font-medium">{t('public_form.common.weight')}:</span>
                                                                <span>{product.weight} {t('public_form.common.kg')}</span>
                                                            </div>
                                                            )}
                                                            <div className="flex justify-between">
                                                                <span className="font-medium">{t('public_form.common.price')}:</span>
                                                                {formatProductPricing(product)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="px-6 pb-6">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleToggleProduct(product.id)}
                                                        className={`w-full py-2 px-4 rounded-md font-medium transition-colors mt-auto ${
                                                            isSelected
                                                                ? 'public-form-primary-bg text-white public-form-primary-hover'
                                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                        }`}
                                                    >
                                                        {isSelected ? t('public_form.common.remove') : t('public_form.common.add')}
                                                    </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Custom Offer Section - Show when product selection is not allowed */}
                        {!allowProductSelection && (
                            <div className="space-y-8">
                                {/* Contact Information and Transport Route - Side by Side on Desktop */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Contact Information Section */}
                                    <div className="bg-white rounded-lg shadow-sm p-6">
                                        <h2 className="text-lg font-semibold text-gray-800 mb-4">{t('public_form.form.contact_info.title')}</h2>
                                        
                                        {isCustomerInfoPresent ? (
                                            <div className="space-y-3">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <p className="font-medium text-gray-900 text-lg">
                                                            {Customer.getCustomerName(order.customer)}
                                                        </p>
                                                        <div className="mt-2 space-y-1">
                                                            <p className="text-base text-gray-600">{order.customer?.contactEmail}</p>
                                                            <p className="text-base text-gray-600">{order.customer?.phoneNumber}</p>
                                                            {order.customer?.primaryAddress?.addressLine && (
                                                                <p className="text-base text-gray-600">
                                                                    {order.customer.primaryAddress.addressLine}, {order.customer.primaryAddress.postCode} {order.customer.primaryAddress.city}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <button type="button" onClick={handleOpenCustomerModal} className="p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors">
                                                        <HiOutlinePencil className="h-5 w-5"/>
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-center py-8">
                                                <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                                                    <HiOutlinePlus className="h-6 w-6 text-muted"/>
                                                </div>
                                                <p className="text-sm text-gray-500 mb-4">{t('public_form.common.add_contact_info')}</p>
                                                <button
                                                    type="button"
                                                    onClick={handleOpenCustomerModal}
                                                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                                                >
                                                    {t('public_form.common.add')}
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Transport Route Section */}
                                    <div className="bg-white rounded-lg shadow-sm p-6">
                                        <h2 className="text-lg font-semibold text-gray-800 mb-4">{hasDefaultPickup ? t('public_form.common.add_delivery_location') : t('public_form.common.add_transport_route')}</h2>
                                        
                                        {visibleRoute.length > 0 ? (
                                            <div className="space-y-3">
                                                {visibleRoute.map(({ item, routeIndex }, index) => (
                                                    <div key={routeIndex} className="flex items-center p-3 bg-gray-50 rounded-lg">
                                                        <div className="flex-shrink-0 mr-3 flex items-center">
                                                            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                                                                <span className="text-xs font-medium text-gray-600">{index + 1}</span>
                                                            </div>
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center justify-between mb-1">
                                                                <p className="text-base font-medium text-gray-900">
                                                                    {item.location.displayName || t('public_form.form.route.unknown_location')}
                                                                </p>
                                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${
                                                                    item.isPickup ? 'public-form-primary-light-bg public-form-primary-text' : 'bg-success-muted text-success'
                                                                }`}>
                                                                    {item.isPickup ? t('public_form.common.pickup') : t('public_form.common.delivery')}
                                                                </span>
                                                            </div>
                                                            {(item.location.addressLine || item.location.postCode || item.location.city) && (
                                                                <div className="flex items-center justify-between">
                                                                    <p className="text-sm text-gray-600">
                                                                        {item.location.addressLine && (
                                                                            <span>{item.location.addressLine}</span>
                                                                        )}
                                                                        {(item.location.postCode || item.location.city) && (
                                                                            <span>
                                                                                {item.location.addressLine && ', '}
                                                                                {item.location.postCode && item.location.city 
                                                                                    ? `${item.location.postCode} ${item.location.city}`
                                                                                    : item.location.postCode || item.location.city
                                                                                }
                                                                            </span>
                                                                        )}
                                                                    </p>
                                                                    <div className="flex items-center space-x-1">
                                                                        <button type="button" onClick={() => handleOpenRouteModal(routeIndex)} className="p-1.5 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors">
                                                                            <HiOutlinePencil className="h-4 w-4"/>
                                                                        </button>
                                                                        <button type="button" onClick={() => handleRemoveRoute(routeIndex)} className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors">
                                                                            <HiOutlineTrash className="h-4 w-4"/>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                                <button 
                                                    type="button" 
                                                    onClick={() => handleOpenRouteModal(null)}
                                                    className="w-full mt-3 py-2 px-3 border border-dashed border-gray-300 rounded-md text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                                                >
                                                    <HiOutlinePlus className="h-4 w-4 inline mr-2"/>
                                                    {t('public_form.common.add_stop')}
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="text-center py-8">
                                                <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                                                    <HiLocationMarker className="h-6 w-6 text-muted"/>
                                                </div>
                                                <p className="text-sm text-gray-500 mb-4">{hasDefaultPickup ? t('public_form.common.add_delivery_location') : t('public_form.common.add_transport_route')}</p>
                                                <button
                                                    type="button"
                                                    onClick={() => handleOpenRouteModal(null)}
                                                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                                                >
                                                    {t('public_form.common.add')}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Description Section */}
                                <div className="bg-white rounded-lg shadow-sm p-8">
                                    <h2 className="text-2xl font-semibold text-gray-800 mb-6">
                                        {config?.customOrderFlowTitle || t('public_form.flow_selection.custom_offer.title')}
                                    </h2>
                                    <p className="text-gray-600 mb-8">
                                        {config?.customOrderFlowDescription || t('public_form.flow_selection.custom_offer.description')}
                                    </p>
                                    
                                    <div>
                                        <label htmlFor="transportDescription" className="block text-lg font-medium text-gray-900 mb-4">
                                            {t('public_form.form.cargo.describe_needs')}
                                        </label>
                                        <textarea 
                                            id="transportDescription" 
                                            value={order.comment || ''} 
                                            onChange={e => dispatch({ type: 'UPDATE_COMMENT', payload: { value: e.target.value } })}
                                            rows={8}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-base"
                                            placeholder={t('public_form.common.describe_transport_needs')}
                                        />
                                        <p className="mt-2 text-sm text-gray-500">
                                            {t('public_form.form.cargo.detailed_info_help')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Proceed Button */}
                        <div>
                            {allowProductSelection ? (
                                <Button 
                                    type="button" 
                                    disabled={!canProceedToDetails}
                                    onClick={handleProceedToDetails}
                                    className="w-full text-lg py-4"
                                    size="xl"
                                    style={{
                                        backgroundColor: config?.primaryColor || '#7214FF',
                                        borderColor: config?.primaryColor || '#7214FF',
                                        color: 'white'
                                    }}
                                    onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                                        if (!canProceedToDetails) return;
                                        e.currentTarget.style.backgroundColor = config?.primaryColorDark || '#5B0ACD';
                                    }}
                                    onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                                        if (!canProceedToDetails) return;
                                        e.currentTarget.style.backgroundColor = config?.primaryColor || '#7214FF';
                                    }}
                                >
                                    {t('public_form.common.go_forward')}
                                </Button>
                            ) : (
                                <>
                                    {/* Validation status indicators for custom offer flow */}
                                    {hasSubmitted && (
                                        <div className="mb-4 flex flex-wrap gap-4">
                                            {!validateContactInfo() && (
                                                <div className="flex items-center text-sm text-red-600">
                                                    <div className="w-4 h-4 rounded-full mr-2 bg-red-500"></div>
                                                    {t('public_form.validation.status.contact_missing')}
                                                </div>
                                            )}
                                            {!validateRouteInfo() && (
                                                <div className="flex items-center text-sm text-red-600">
                                                    <div className="w-4 h-4 rounded-full mr-2 bg-red-500"></div>
                                                    {t('public_form.validation.status.route_missing')}
                                                </div>
                                            )}
                                            {allowProductSelection && selectedProducts.length === 0 && (
                                                <div className="flex items-center text-sm text-red-600">
                                                    <div className="w-4 h-4 rounded-full mr-2 bg-red-500"></div>
                                                    {t('public_form.validation.status.no_products')}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <Button 
                                        type="submit" 
                                        disabled={isLoading}
                                        onClick={handleFormSubmit}
                                        className="w-full text-lg py-4"
                                        size="xl"
                                        style={{
                                            backgroundColor: config?.primaryColor || '#7214FF',
                                            borderColor: config?.primaryColor || '#7214FF',
                                            color: 'white'
                                        }}
                                        onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                                            if (isLoading) return;
                                            e.currentTarget.style.backgroundColor = config?.primaryColorDark || '#5B0ACD';
                                        }}
                                        onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                                            if (isLoading) return;
                                            e.currentTarget.style.backgroundColor = config?.primaryColor || '#7214FF';
                                        }}
                                    >
                                        {isLoading ? t('public_form.common.sending') : (selectedFlow === 'product-list' ? t('public_form.common.send_order') : t('public_form.common.send_request'))}
                                    </Button>
                                </>
                            )}
                            {!canProceedToDetails && allowProductSelection && (
                                <p className="text-center text-sm text-gray-500 mt-2">
                                    {t('public_form.validation.no_products_selected')}
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* Step 2: Contact and Route Information */}
                {currentStep === 2 && (
                    <form onSubmit={handleFormSubmit} className="space-y-12">
                        {/* Contact Information and Transport Route - Side by Side on Desktop */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Contact Information Section */}
                            <div className="bg-white rounded-lg shadow-sm p-6">
                                <h2 className="text-lg font-semibold text-gray-800 mb-4">{t('public_form.form.contact_info.title')}</h2>
                                
                                {isCustomerInfoPresent ? (
                                    <div className="space-y-3">
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <p className="font-medium text-gray-900 text-lg">
                                                    {Customer.getCustomerName(order.customer)}
                                                </p>
                                                <div className="mt-2 space-y-1">
                                                    <p className="text-base text-gray-600">{order.customer?.contactEmail}</p>
                                                    <p className="text-base text-gray-600">{order.customer?.phoneNumber}</p>
                                                    {order.customer?.primaryAddress?.addressLine && (
                                                        <p className="text-base text-gray-600">
                                                            {order.customer.primaryAddress.addressLine}, {order.customer.primaryAddress.postCode} {order.customer.primaryAddress.city}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <button type="button" onClick={handleOpenCustomerModal} className="p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors">
                                                <HiOutlinePencil className="h-5 w-5"/>
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-8">
                                        <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                                            <HiOutlinePlus className="h-6 w-6 text-muted"/>
                                        </div>
                                        <p className="text-sm text-gray-500 mb-4">{t('public_form.common.add_contact_info')}</p>
                                        <button
                                            type="button"
                                            onClick={handleOpenCustomerModal}
                                            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                                        >
                                            {t('public_form.common.add')}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Transport Route Section */}
                            <div className="bg-white rounded-lg shadow-sm p-6">
                                <h2 className="text-lg font-semibold text-gray-800 mb-4">{hasDefaultPickup ? t('public_form.common.add_delivery_location') : t('public_form.common.add_transport_route')}</h2>
                                
                                {visibleRoute.length > 0 ? (
                                    <div className="space-y-3">
                                        {visibleRoute.map(({ item, routeIndex }, index) => (
                                            <div key={routeIndex} className="flex items-center p-3 bg-gray-50 rounded-lg">
                                                <div className="flex-shrink-0 mr-3 flex items-center">
                                                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                                                        <span className="text-xs font-medium text-gray-600">{index + 1}</span>
                                                    </div>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between mb-1">
                                                        <p className="text-base font-medium text-gray-900">
                                                            {item.location.displayName || t('public_form.form.route.unknown_location')}
                                                        </p>
                                                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${
                                                            item.isPickup ? 'public-form-primary-light-bg public-form-primary-text' : 'bg-success-muted text-success'
                                                        }`}>
                                                            {item.isPickup ? t('public_form.common.pickup') : t('public_form.common.delivery')}
                                                        </span>
                                                    </div>
                                                    {(item.location.addressLine || item.location.postCode || item.location.city) && (
                                                        <div className="flex items-center justify-between">
                                                            <p className="text-sm text-gray-600">
                                                                {item.location.addressLine && (
                                                                    <span>{item.location.addressLine}</span>
                                                                )}
                                                                {(item.location.postCode || item.location.city) && (
                                                                    <span>
                                                                        {item.location.addressLine && ', '}
                                                                        {item.location.postCode && item.location.city 
                                                                            ? `${item.location.postCode} ${item.location.city}`
                                                                            : item.location.postCode || item.location.city
                                                                        }
                                                                    </span>
                                                                )}
                                                            </p>
                                                            <div className="flex items-center space-x-1">
                                                                <button type="button" onClick={() => handleOpenRouteModal(routeIndex)} className="p-1.5 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors">
                                                                    <HiOutlinePencil className="h-4 w-4"/>
                                                                </button>
                                                                <button type="button" onClick={() => handleRemoveRoute(routeIndex)} className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors">
                                                                    <HiOutlineTrash className="h-4 w-4"/>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                        <button 
                                            type="button" 
                                            onClick={() => handleOpenRouteModal(null)}
                                            className="w-full mt-3 py-2 px-3 border border-dashed border-gray-300 rounded-md text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                                        >
                                            <HiOutlinePlus className="h-4 w-4 inline mr-2"/>
                                            {t('public_form.common.add_stop')}
                                        </button>
                                    </div>
                                ) : (
                                    <div className="text-center py-8">
                                        <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                                            <HiLocationMarker className="h-6 w-6 text-muted"/>
                                        </div>
                                        <p className="text-sm text-gray-500 mb-4">{hasDefaultPickup ? t('public_form.common.add_delivery_location') : t('public_form.common.add_transport_route')}</p>
                                        <button
                                            type="button"
                                            onClick={() => handleOpenRouteModal(null)}
                                            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                                        >
                                            {t('public_form.common.add')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Order Summary (readonly in step 2) */}
                        {selectedProducts.length > 0 && (
                            <div className="bg-white rounded-lg shadow-sm p-8">
                                <h2 className="text-2xl font-semibold text-gray-800 mb-6">{t('public_form.common.price_details')}</h2>
                                
                                {/* Price Items Table */}
                                <div className="overflow-x-auto mb-4">
                                    <table className="w-full table-auto text-sm">
                                        <thead>
                                            <tr className="border-b-2 border-gray-200">
                                                <th className="text-left py-2 px-2 font-medium text-gray-800 w-3/12 text-sm">
                                                    {t('public_form.common.description')}
                                                </th>
                                                <th className="text-center py-2 px-2 font-medium text-gray-800 w-1/12 text-sm">
                                                    {t('public_form.common.quantity')}
                                                </th>
                                                <th className="text-center py-2 px-2 font-medium text-gray-800 w-2/12 text-sm">
                                                    {t('common:from')}
                                                </th>
                                                <th className="text-center py-2 px-2 font-medium text-gray-800 w-2/12 text-sm">
                                                    {t('common:to')}
                                                </th>
                                                <th className="text-right py-2 px-2 font-medium text-gray-800 w-1/12 text-sm">
                                                    {t('public_form.common.price')}
                                                </th>
                                                <th className="text-right py-2 px-2 font-medium text-gray-800 w-1/12 text-sm">
                                                    {t('public_form.common.vat')}
                                                </th>
                                                <th className="text-right py-2 px-2 font-medium text-gray-800 w-2/12 text-sm">
                                                    {t('public_form.common.total')}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                    {selectedProducts.map((item, index) => {
                                        const product = products.find(p => p.id === item.productId);
                                                const quantity = item.quantity || 1;
                                                
                                                // Use the already calculated price from the item
                                                const productTotal = item.price || 0;
                                                
                                                // Create price breakdown for display
                                                const priceBreakdown: string[] = [];
                                                if (product?.pricing?.pricePerCargoUnit && product.pricing.pricePerCargoUnit > 0) {
                                                    priceBreakdown.push(`${product.pricing.pricePerCargoUnit.toFixed(2)} ${t('public_form.common.currency')}/${t('public_form.common.pieces_short')} × ${quantity}`);
                                                }
                                                if (product?.pricing?.pricePerDistanceUnit && product.pricing.pricePerDistanceUnit > 0 && cachedDistance > 0) {
                                                    priceBreakdown.push(`${cachedDistance.toFixed(2)} ${t('public_form.common.km')} × ${product.pricing.pricePerDistanceUnit.toFixed(2)} ${t('public_form.common.currency')}/${t('public_form.common.km')}`);
                                                }
                                                if (product?.pricing?.pricePerVolumeUnit && product.pricing.pricePerVolumeUnit > 0) {
                                                    const volume = ((product.length * product.width * product.height) / 1000000); // Convert cm³ to m³
                                                    if (volume > 0) {
                                                        priceBreakdown.push(`${volume.toFixed(2)} ${t('public_form.common.m3')} × ${product.pricing.pricePerVolumeUnit.toFixed(2)} ${t('public_form.common.currency')}/${t('public_form.common.m3')}`);
                                                    }
                                                }
                                                if (product?.pricing?.pricePerWeightUnit && product.pricing.pricePerWeightUnit > 0 && product.weight && product.weight > 0) {
                                                    priceBreakdown.push(`${product.weight} ${t('public_form.common.kg')} × ${product.pricing.pricePerWeightUnit.toFixed(2)} ${t('public_form.common.currency')}/${t('public_form.common.kg')}`);
                                                }
                                                if (product?.pricing?.pricePerTimeUnit && product.pricing.pricePerTimeUnit > 0) {
                                                    const timePeriod = product.pricing.timePeriodInMinutes || 60;
                                                    const timeUnit = timePeriod === 1 ? t('public_form.common.minute') : 
                                                                    timePeriod === 60 ? t('public_form.common.hour') : 
                                                                    `${timePeriod} ${t('public_form.common.minutes_short')}`;
                                                    priceBreakdown.push(`${product.pricing.pricePerTimeUnit.toFixed(2)} ${t('public_form.common.currency')}/${timeUnit}`);
                                                }
                                                if (priceBreakdown.length === 0) {
                                                    priceBreakdown.push(`${formatPrice(productTotal)}`);
                                                }
                                                
                                                const vatRate = product?.pricing?.vat || 0;
                                                const lineSubtotal = productTotal;
                                                const lineVatAmount = lineSubtotal * (vatRate / 100);
                                                const lineTotalWithVat = lineSubtotal + lineVatAmount;

                                                // Get available stops for dropdowns
                                                const pickupStops = getPickupStops();
                                                const deliveryStops = getDeliveryStops();
                                                const currentPickupStop = item.itinerary?.pickupStopNumber ?? 0;
                                                const currentDeliveryStop = item.itinerary?.deliveryStopNumber ?? 1;
                                                const hasTimePricing = !!(
                                                    product?.pricing?.priceModel?.includes('Time') &&
                                                    ((product?.pricing?.pricePerTimeUnit || 0) > 0)
                                                );
                                                
                                        return (
                                                    <>
                                                    <tr key={(item.productId || index) + '-main'} className="border-b border-gray-100 hover:bg-gray-50">
                                                        <td className="py-3 px-2 text-gray-700 font-medium text-sm">
                                                            {item.descriptionShort || 'N/A'}
                                                        </td>
                                                        <td className="py-3 px-2 text-center text-gray-600 text-sm">
                                            <div className="flex items-center justify-center">
                                                <div className="flex items-center border border-gray-300 rounded overflow-hidden">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const currentQuantity = item.quantity || 1;
                                                            if (currentQuantity > 1 && item.productId) {
                                                                handleQuantityChange(item.productId, currentQuantity - 1);
                                                            }
                                                        }}
                                                        disabled={!item.productId || (item.quantity || 1) <= 1}
                                                        className="px-2 py-1 bg-gray-50 hover:bg-surface-muted text-secondary disabled:text-muted disabled:cursor-not-allowed focus:outline-none transition-colors"
                                                    >
                                                        −
                                                    </button>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={item.quantity || 1}
                                                        onChange={(e) => {
                                                            const newQuantity = Math.max(1, parseInt(e.target.value) || 1);
                                                            if (item.productId) {
                                                                handleQuantityChange(item.productId, newQuantity);
                                                            }
                                                        }}
                                                        className="w-12 px-1 py-1 text-sm text-center border-0 border-l border-r border-gray-300 focus:ring-0 focus:border-indigo-500 focus:z-10 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const currentQuantity = item.quantity || 1;
                                                            if (item.productId) {
                                                                handleQuantityChange(item.productId, currentQuantity + 1);
                                                            }
                                                        }}
                                                        disabled={!item.productId}
                                                        className="px-2 py-1 bg-gray-50 hover:bg-surface-muted text-secondary disabled:text-muted disabled:cursor-not-allowed focus:outline-none transition-colors"
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                                <span className="ml-1 text-xs text-gray-500">{t('public_form.common.pieces_short')}</span>
                                            </div>
                                        </td>
                                                        {/* Pickup Location Dropdown */}
                                                        <td className="py-3 px-2 text-center text-gray-600 text-sm">
                                                            {pickupStops.length > 0 ? (
                                                                <select
                                                                    value={currentPickupStop}
                                                                    onChange={(e) => {
                                                                        if (item.productId) {
                                                                            handlePickupLocationChange(item.productId, parseInt(e.target.value));
                                                                        }
                                                                    }}
                                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                                                                >
                                                                    {pickupStops.map((stop) => {
                                                                        const displayInfo = formatStopDisplayName(stop);
                                                                        return (
                                                                            <option key={stop.stopNumber} value={stop.stopNumber}>
                                                                                {displayInfo.label}
                                                                            </option>
                                                                        );
                                                                    })}
                                                                </select>
                                                            ) : (
                                                                <span className="text-muted text-xs">{t('public_form.common.no_pickup_stops')}</span>
                                                            )}
                                                        </td>
                                                        {/* Delivery Location Dropdown */}
                                                        <td className="py-3 px-2 text-center text-gray-600 text-sm">
                                                            {deliveryStops.length > 0 ? (
                                                                <select
                                                                    value={currentDeliveryStop}
                                                                    onChange={(e) => {
                                                                        if (item.productId) {
                                                                            handleDeliveryLocationChange(item.productId, parseInt(e.target.value));
                                                                        }
                                                                    }}
                                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                                                                >
                                                                    {deliveryStops.map((stop) => {
                                                                        const displayInfo = formatStopDisplayName(stop);
                                                                        return (
                                                                            <option key={stop.stopNumber} value={stop.stopNumber}>
                                                                                {displayInfo.label}
                                                                            </option>
                                                                        );
                                                                    })}
                                                                </select>
                                                            ) : (
                                                                <span className="text-muted text-xs">{t('public_form.common.no_delivery_stops')}</span>
                                                            )}
                                                        </td>
                                                        <td className="py-3 px-2 text-right text-gray-700 text-sm">
                                                <div className="text-right">
                                                                {priceBreakdown.map((breakdown, idx) => (
                                                                    <div key={idx} className="text-xs text-gray-600">{breakdown}</div>
                                                                ))}
                                                </div>
                                                        </td>
                                                        <td className="py-3 px-2 text-right text-gray-600 text-sm">
                                                            {vatRate > 0 ? `${vatRate}%` : '-'}
                                                        </td>
                                                        <td className="py-3 px-2 text-right text-gray-700 font-medium text-sm">
                                                            {config?.showPricesWithVat && vatRate > 0 ? formatPrice(lineTotalWithVat) : formatPrice(lineSubtotal)}
                                                        </td>
                                                    </tr>
                                                    {hasTimePricing && (
                                                        <tr key={(item.productId || index) + '-time'} className="border-b border-gray-100 bg-gray-50">
                                                            <td className="py-3 px-2 text-gray-700 font-medium text-sm">
                                                                <div className="flex items-center">
                                                                    <HiOutlineClock className="mr-2 h-4 w-4 flex-shrink-0" />
                                                                    {t('public_form.common.time_price_row', { name: product?.descriptionShort || t('public_form.success.unknown_product') })}
                                                                </div>
                                                            </td>
                                                            <td className="py-3 px-2 text-center text-gray-600 text-sm">
                                                                -
                                                            </td>
                                                            <td className="py-3 px-2 text-center text-gray-600 text-sm">
                                                                -
                                                            </td>
                                                            <td className="py-3 px-2 text-center text-gray-600 text-sm">
                                                                -
                                                            </td>
                                                            <td className="py-3 px-2 text-right text-gray-700 text-sm whitespace-nowrap">
                                                                {(product?.pricing?.pricePerTimeUnit || 0)} {t('public_form.common.currency')}/{(product?.pricing?.timePeriodInMinutes || 60)} {t('public_form.common.minutes_short', 'min')} {t('common:excl_vat', 'eks. mva')}
                                                            </td>
                                                            <td className="py-3 px-2 text-right text-gray-600 text-sm">
                                                                {vatRate > 0 ? `${vatRate}%` : '-'}
                                                            </td>
                                                            <td className="py-3 px-2 text-right text-gray-700 font-medium text-sm">
                                                                {t('common:calculated_label', 'Blir beregnet')}
                                                            </td>
                                                        </tr>
                                                    )}
                                                    </>
                                        );
                                    })}
                                        </tbody>
                                    </table>
                                        </div>
                        
                                {/* Grid for Comment, Project Number & Summary */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                    {/* Column 1: Comment and Project Number */}
                                    <div className="space-y-4">
                        {/* Comment Section */}
                                        <div>
                                            <h3 className="font-medium text-base mb-2 text-gray-900">{t('public_form.common.comment')}</h3>
                            <textarea 
                                                className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-indigo-500 focus:border-indigo-500"
                                                rows={3}
                                                value={order.comment || ''} 
                                                onChange={(e) => dispatch({ type: 'UPDATE_COMMENT', payload: { value: e.target.value } })}
                                placeholder={t('public_form.common.write_comment_here')}
                            />
                        </div>
                                        
                                        {/* Project Number Section */}
                                        <div>
                                            <h3 className="font-medium text-base mb-2 text-gray-900">{t('public_form.common.project_number')}</h3>
                                            <input
                                                type="text"
                                                className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-indigo-500 focus:border-indigo-500"
                                                value={order.projectNumber || ''}
                                                placeholder={t('public_form.common.enter_project_number')}
                                                onChange={(e) => dispatch({ type: 'UPDATE_PROJECT_NUMBER', payload: { value: e.target.value } })}
                                            />
                                        </div>
                                    </div>

                                    {/* Column 2: Summary Totals */}
                                    <div className="w-full text-sm">
                                        {(() => {
                                            const hasTimePricedProducts = selectedProducts.some((orderItem) => {
                                                const p = products.find(pp => pp.id === orderItem.productId);
                                                return !!(p?.pricing?.priceModel?.includes('Time') && ((p?.pricing?.pricePerTimeUnit || 0) > 0));
                                            });

                                            const subtotal = calculateTotalPrice();
                                            const totalVat = selectedProducts.reduce((sum, orderItem) => {
                                                const product = products.find(p => p.id === orderItem.productId);
                                                const vatRate = (product?.pricing?.vat) || 0;
                                                return sum + ((orderItem.price || 0) * (vatRate / 100));
                                            }, 0);
                                            const hasVat = totalVat > 0;
                                            const total = subtotal + totalVat;

                                            return (
                                                <>
                                                    <div className="grid grid-cols-2 gap-x-5 gap-y-2">
                                                        {hasVat ? (
                                                            <>
                                                                {/* Total excluding VAT */}
                                                                <div className="text-gray-600 whitespace-nowrap text-left">
                                                                    {t('public_form.common.total_excl_vat', 'Total eks. MVA')}:
                                                                </div>
                                                                <div className="font-medium text-gray-800 text-right">
                                                                    {formatPrice(subtotal)}
                                                                </div>
                                                                {/* VAT amount */}
                                                                <div className="text-gray-600 whitespace-nowrap text-left">
                                                                    {t('public_form.common.vat', 'MVA')}:
                                                                </div>
                                                                <div className="font-medium text-gray-800 text-right">
                                                                    {formatPrice(totalVat)}
                                                                </div>
                                                                {/* Total including VAT */}
                                                                <div className="text-gray-900 whitespace-nowrap text-left font-bold pt-3 border-t border-gray-200 mt-1 text-base">
                                                                    {t('public_form.common.total_incl_vat', 'Total inkl. MVA')}:
                                                                </div>
                                                                <div className="text-gray-900 text-right font-bold pt-3 border-t border-gray-200 mt-1 text-base">
                                                                    {formatPrice(total)}
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div className="text-gray-600 whitespace-nowrap text-left">
                                                                    {t('public_form.common.subtotal')}:
                                                                </div>
                                                                <div className="font-medium text-gray-800 text-right">
                                                                    {formatPrice(subtotal)}
                                                                </div>
                                                                <div className="text-gray-900 whitespace-nowrap text-left font-bold pt-3 border-t border-gray-200 mt-1 text-base">
                                                                    {t('public_form.common.total')}:
                                                                </div>
                                                                <div className="text-gray-900 text-right font-bold pt-3 border-t border-gray-200 mt-1 text-base">
                                                                    {formatPrice(subtotal)}
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                    {hasTimePricedProducts && (
                                                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-yellow-800 mt-3">
                                                            {t('public_form.common.time_price_disclaimer')}
                                                        </div>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>
                        )}

                        
                        {/* Submit Section */}
                        <div>
                            <Button 
                                type="submit" 
                                disabled={isLoading}
                                onClick={handleFormSubmit}
                                className="w-full text-lg py-4"
                                size="xl"
                                style={{
                                    backgroundColor: config?.primaryColor || '#7214FF',
                                    borderColor: config?.primaryColor || '#7214FF',
                                    color: 'white'
                                }}
                                onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                                    if (isLoading) return;
                                    e.currentTarget.style.backgroundColor = config?.primaryColorDark || '#5B0ACD';
                                }}
                                onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                                    if (isLoading) return;
                                    e.currentTarget.style.backgroundColor = config?.primaryColor || '#7214FF';
                                }}
                            >
                                {isLoading ? t('public_form.common.sending') : (selectedFlow === 'product-list' ? t('public_form.common.send_order') : t('public_form.common.send_request'))}
                            </Button>
                        </div>
                    </form>
                )}
            </div>
        </div>
        </div>
    );
};

export default PublicOrderForm; 
